import { getPromptAndInternalMessages } from 'quadratic-shared/ai/helpers/message.helper';
import type { Chat } from 'quadratic-shared/typesAndSchemasAI';

/**
 * A chat's transcript length measured with the same filter the API persists and
 * advertises, so every growth comparison (advertised presence count,
 * fetch-newer guard, list merge) agrees on one measure.
 */
export const transcriptLength = (chat: Chat): number => getPromptAndInternalMessages(chat.messages).length;

/** Local transcript + server metadata (name, author). */
export const withServerMetadata = (local: Chat, server: Chat): Chat => ({
  ...local,
  name: server.name,
  author: server.author,
});

/**
 * Reconcile the server's chat list with the copy of the open chat this client
 * holds locally.
 *
 * The server is authoritative for which chats exist on a file and for the
 * metadata shared with collaborators (name, author), so chats created, renamed,
 * or deleted by a collaborator appear, update, and disappear here.
 *
 * The open chat is the one exception. A transcript only reaches the server once
 * a turn completes, so the chat being worked on can be ahead of the server's
 * copy or missing from it entirely — taking the server list verbatim would drop
 * in-flight work, and on a reload would reopen the panel on a blank chat. Its
 * local transcript is therefore kept whenever it is at least as long as the
 * server's, with the server's metadata layered on top.
 *
 * `openChat` is the local copy of the open thread: the current chat during a
 * live refresh, and the session-restored thread during initialization. Both
 * entry points share this policy so the list does not change shape depending on
 * which one produced it.
 */
export const mergeChatLists = (serverChats: Chat[], openChat: Chat | undefined): Chat[] => {
  const byId = new Map(serverChats.map((chat) => [chat.id, chat]));
  if (openChat?.id) {
    const server = byId.get(openChat.id);
    if (!server || transcriptLength(openChat) >= transcriptLength(server)) {
      byId.set(openChat.id, server ? withServerMetadata(openChat, server) : openChat);
    }
  }
  return Array.from(byId.values());
};
